// /episode-image/ — draws a podcast episode's feature image on a canvas
// and saves it as a JPEG. Same artwork as the back catalogue (made by
// brand/share-cards/ tooling): night-teal ground, title-seeded
// waveform, swell lines, text kept inside the centre 3:2 that phones
// crop to. 1200 × 514 design units, drawn at 2× = 2400 × 1028.

const W = 1200;
const H = 514;
const SAFE = H * 1.5;
const L = (W - SAFE) / 2;
const COLOURS = {
    top: "#0e4f64",
    bottom: "#001f2b",
    glow1: "rgba(45,143,158,.55)",
    glow2: "rgba(0,49,81,.7)",
    wave: "rgba(207,235,236,.45)",
    swell: "#b1d5dc",
    ink: "#f8f5ee",
};
const DISPLAY = '"Newsreader", Georgia, serif';
const MONO = '"JetBrains Mono", Menlo, monospace';
const STORE = "gg-episode-image";

function seeded(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
    return () => {
        h = Math.imul(h ^ (h >>> 15), h | 1);
        h ^= h + Math.imul(h ^ (h >>> 7), h | 61);
        return ((h ^ (h >>> 14)) >>> 0) / 4294967296;
    };
}

function fontSize(title) {
    const n = title.length;
    return n <= 22 ? 72 : n <= 34 ? 62 : n <= 48 ? 54 : 46;
}

// Break on spaces only, so hyphenated words stay whole; fewest lines
// that fit, as even as possible.
function wrap(ctx, text, maxW) {
    const words = text.split(/\s+/).filter(Boolean);
    for (let lines = 1; lines <= 4; lines++) {
        let lo = 0;
        let hi = maxW;
        let fit = null;
        for (let i = 0; i < 20; i++) {
            const mid = (lo + hi) / 2;
            const out = [];
            let cur = "";
            for (const w of words) {
                const next = cur ? `${cur} ${w}` : w;
                if (cur && ctx.measureText(next).width > mid) {
                    out.push(cur);
                    cur = w;
                } else {
                    cur = next;
                }
            }
            if (cur) out.push(cur);
            if (out.length <= lines) {
                fit = out;
                hi = mid;
            } else {
                lo = mid;
            }
        }
        if (fit && fit.every((l) => ctx.measureText(l).width <= maxW)) return fit;
    }
    return [text];
}

function niceDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }).replace(".", "");
}

function draw(ctx, s) {
    const title = s.title || "Episode title";
    ctx.setTransform(2, 0, 0, 2, 0, 0);

    const ground = ctx.createLinearGradient(0, 0, W * 0.57, H * 1.6);
    ground.addColorStop(0, COLOURS.top);
    ground.addColorStop(1, COLOURS.bottom);
    ctx.fillStyle = ground;
    ctx.fillRect(0, 0, W, H);
    for (const [x, y, r, col] of [[W * 0.3, H * 0.2, W * 0.55, COLOURS.glow1], [W * 0.75, H * 0.85, W * 0.6, COLOURS.glow2]]) {
        const glow = ctx.createRadialGradient(x, y, 0, x, y, r);
        glow.addColorStop(0, col);
        glow.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, W, H);
    }

    const rand = seeded(title);
    const bars = 150;
    let x = L + (SAFE - bars * 4.4) / 2;
    ctx.fillStyle = COLOURS.wave;
    for (let i = 0; i < bars; i++) {
        const env = Math.pow(Math.sin((Math.PI * i) / bars), 0.6);
        const a = Math.max(2, 120 * env * (0.25 + 0.75 * rand()));
        ctx.fillRect(x, 420 - a / 2, 2.4, a);
        x += 4.4;
    }

    const swell = seeded(`${title}~`);
    ctx.strokeStyle = COLOURS.swell;
    for (let k = 0; k < 3; k++) {
        ctx.globalAlpha = 0.35 - k * 0.08;
        ctx.lineWidth = 1.4 - k * 0.35;
        const y = 486 + k * 14;
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let px = 0; px < W; px += 72) {
            const a = 7 * (0.6 + 0.4 * swell()) * (1 - k * 0.25);
            ctx.quadraticCurveTo(px + 18, y - a, px + 36, y);
            ctx.quadraticCurveTo(px + 54, y + a, px + 72, y);
        }
        ctx.stroke();
    }

    const tx = L + 20;
    ctx.fillStyle = COLOURS.ink;
    ctx.globalAlpha = 0.78;
    ctx.font = `400 12px ${MONO}`;
    ctx.letterSpacing = "1.9px";
    ctx.fillText(s.kind === "guest" ? "GRANT ON ANOTHER SHOW" : `THE ROARING HEADS${s.num ? ` · EPISODE ${s.num}` : ""}`, tx, 76);

    const size = fontSize(title);
    ctx.globalAlpha = 1;
    ctx.font = `300 ${size}px ${DISPLAY}`;
    ctx.letterSpacing = `${-size * 0.025}px`;
    let y = 94 + size * 0.8;
    const lines = wrap(ctx, title, SAFE - 40);
    lines.forEach((line, i) => ctx.fillText(line, tx, y + i * size * 0.98));
    y += (lines.length - 1) * size * 0.98;

    ctx.letterSpacing = "0px";
    ctx.globalAlpha = 0.85;
    ctx.font = `italic 400 21px ${DISPLAY}`;
    ctx.fillText(niceDate(s.date), tx, y + size * 0.2 + 31);
    ctx.globalAlpha = 1;
}

function filename(s) {
    const slug = (s.title || "episode").toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
    return `${s.kind === "guest" ? "guest-" : s.num ? `rh-${s.num}-` : "rh-"}${slug}.jpg`;
}

export default function episodeImageMaker() {
    const root = document.querySelector("[data-image-maker]");
    if (!root) return;

    const $ = (sel) => root.querySelector(sel);
    const form = $("[data-maker-form]");
    const canvas = $("[data-maker-canvas]");
    const ctx = canvas.getContext("2d");
    const num = $("#maker-num");
    const title = $("#maker-title");
    const date = $("#maker-date");
    const status = $("[data-maker-status]");
    const alt = $("[data-maker-alt]");

    const read = () => ({
        kind: form.querySelector('input[name="kind"]:checked').value,
        num: num.value.trim(),
        title: title.value.replace(/\s+/g, " ").trim(),
        date: date.value,
    });

    const render = () => {
        const s = read();
        $("[data-maker-num-wrap]").hidden = s.kind === "guest";
        draw(ctx, s);
        alt.textContent = `${s.kind === "guest" ? "Grant Giles on another show" : `The Roaring Heads${s.num ? `, episode ${s.num}` : ""}`}: ${s.title || "Episode title"}`;
        try {
            localStorage.setItem(STORE, JSON.stringify(s));
        } catch {
            // Private browsing: nothing to remember, the tool still works.
        }
    };

    // Pick up where Grant left off on this device; otherwise today's date
    try {
        const saved = JSON.parse(localStorage.getItem(STORE) || "null");
        if (saved && saved.title) {
            form.querySelector(`#maker-kind-${saved.kind === "guest" ? "guest" : "rh"}`).checked = true;
            num.value = saved.num || "";
            title.value = saved.title;
            date.value = saved.date || "";
        }
    } catch {
        // Ignore unreadable storage.
    }
    if (!date.value) date.value = new Date().toLocaleDateString("en-CA");

    form.addEventListener("input", render);
    form.addEventListener("submit", (e) => e.preventDefault());
    $("#maker-crop").addEventListener("change", (e) => {
        $("[data-maker-crop]").hidden = !e.target.checked;
    });

    $("[data-maker-copy]").addEventListener("click", async (e) => {
        const btn = e.currentTarget;
        try {
            await navigator.clipboard.writeText(alt.textContent);
            btn.textContent = "Copied";
        } catch {
            const range = document.createRange();
            range.selectNodeContents(alt);
            getSelection().removeAllRanges();
            getSelection().addRange(range);
            btn.textContent = "Selected";
        }
        setTimeout(() => {
            btn.textContent = "Copy";
        }, 1600);
    });

    $("[data-maker-save]").addEventListener("click", () => {
        const s = read();
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    status.textContent = "Couldn’t make the image. Try again.";
                    return;
                }
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = filename(s);
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 4000);
                status.textContent = `Saved ${a.download}. Now add it in Ghost.`;
            },
            "image/jpeg",
            0.88
        );
    });

    render();
    // Redraw once the site's fonts are ready, so the canvas uses them
    Promise.all([
        document.fonts.load(`300 72px ${DISPLAY}`),
        document.fonts.load(`italic 400 21px ${DISPLAY}`),
        document.fonts.load(`400 12px ${MONO}`),
    ]).then(render, render);
}
